// apps/admin/src/api/admin-users.api.ts
//
// Phase 8.2 Wave 7 Task 49 — Admin Users API client.
// Mirrors finance.api.ts pattern (envelope unwrap + Idempotency-Key for
// mutations) for the System > Users surface owned by `system:manage_admins`
// (and the deprecated alias `user:manage`).
//
// vg-binding: PLAN:task-49
// vg-binding: P8.D-98   — invite-email flow (no password input)
// vg-binding: P8.D-99   — 3 submit modes (create+invite / create no-invite /
//                          invite-by-email link only)
// vg-binding: P8.D-380  — success toast copy "Đã tạo tài khoản và gửi email mời"
// vg-binding: P8.D-474  — Idempotency-Key client-side for invite resend safety
// vg-binding: P8.D-475  — self-action guard (cannot edit/disable self)
// vg-binding: P8.D-486  — error envelope unwrap (user_message > message)
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority
//
// Transport contract:
//   • Reuses the same envelope semantics as finance.api.ts. The shared
//     `apiClient` in packages/shared unwraps `{ok, data}` AND throws on
//     non-2xx — we expose a local thin wrapper here so the React-Query
//     layer can speak in plain payload types and the dialog/page wires
//     don't have to repeat the envelope-unwrap dance.
//   • POST /admin/users/:id/invite is the "resend" surface — Idempotency-Key
//     is required so a click-jittered double-send credits the
//     same audit row instead of duplicating emails.

import axios from 'axios';
import { z } from 'zod';

import { API_BASE_URL, getCsrfToken } from '../lib/apiClient.js';

// ---------------------------------------------------------------------------
// Wire schemas — mirror task-48 BE handler envelope.
// ---------------------------------------------------------------------------

/**
 * Admin user row as returned by GET /admin/users.
 * Mirrors the existing Phase 1 `adminUsers.handler.ts` serializer plus the
 * Phase 8.2 task-48 extension fields (last-active markers stay on
 * the activity endpoint, not the list row).
 */
export const AdminUserRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string().nullable().optional().default(''),
  primaryRole: z.string(),
  roles: z.array(z.string()).optional().default([]),
  status: z.string(),
  createdAt: z.string(),
  lastLoginAt: z.string().nullable().optional(),
});
export type AdminUserRow = z.infer<typeof AdminUserRowSchema>;

export const AdminUserListMetaSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0).optional(),
});
export type AdminUserListMeta = z.infer<typeof AdminUserListMetaSchema>;

export interface AdminUserListPayload {
  rows: AdminUserRow[];
  meta: AdminUserListMeta;
}

/**
 * Filter shape consumed by the Users list page. Each field is optional —
 * the server treats missing keys as "all". `search` is debounced FE-side
 * so the cache slice fingerprint only flips on a stable string.
 */
export interface AdminUserListFilters {
  /** Search by email / display name. Debounced 300ms client-side. */
  search?: string;
  /** Tier-1 role filter — see ROLE_OPTIONS in AdminUsersPage. */
  role?: string;
  /** Status filter — see STATUS_OPTIONS in AdminUsersPage. */
  status?: string;
  page?: number;
  limit?: number;
  /** Server-supported sort field (createdAt|displayName|email|lastLoginAt). */
  sortBy?: string;
  /** asc | desc. */
  sortOrder?: string;
}

/** Request body for POST /admin/users.
 *
 * NOTE: `sendInvite` is REQUIRED (no zod `.default()`) so the schema's
 * input + output types match — the RHF `Resolver<TIn, TCtx, TOut>` typing
 * trips when the two diverge. The dialog supplies `true` via
 * `defaultValues` instead.
 */
export const CreateAdminUserRequestSchema = z.object({
  email: z
    .string()
    .email('Invalid email')
    .max(320, 'Email must be at most 320 characters'),
  displayName: z
    .string()
    .min(2, 'Display name must be at least 2 characters')
    .max(120, 'Display name must be at most 120 characters'),
  /**
   * Single primary role for the new account. The CreateAdminUserDialog
   * mirrors the Phase 1 GRANT_REVOKE_ROLE_TYPES enum (admin | support |
   * saler | manager | accountant | affiliate). Superadmin is intentionally
   * excluded from the create-flow picker.
   */
  roleType: z.enum([
    'admin',
    'support',
    'saler',
    'manager',
    'accountant',
    'affiliate',
  ]),
  /**
   * when true (dialog default), the BE generates an invite token
   * and sends the email. Password is NEVER taken on the FE — invite-only
   * flow. When false (no-invite mode), the account is created in
   * `pending_otp` with no email sent; admin can later "Invite by email"
   * from the row.
   */
  sendInvite: z.boolean(),
});
export type CreateAdminUserRequest = z.infer<typeof CreateAdminUserRequestSchema>;

/** Minimal translator shape — mirrors `useTranslation('admin').t`. */
type Translator = (key: string, vars?: Record<string, unknown>) => string;

/**
 * i18n factory for the create-admin-user form schema. RHF dialogs build this
 * with a bound `t` (useMemo on [t]) so field-validation copy is localized.
 * The const `CreateAdminUserRequestSchema` above stays as the VI fallback used
 * by the internal `createAdminUser` guard (non-rendered .parse()).
 */
export function getCreateAdminUserRequestSchema(t: Translator) {
  return z.object({
    email: z
      .string()
      .email(t('apiMessages.adminUsers.validation.emailInvalid'))
      .max(320, t('apiMessages.adminUsers.validation.emailMax')),
    displayName: z
      .string()
      .min(2, t('apiMessages.adminUsers.validation.displayNameMin'))
      .max(120, t('apiMessages.adminUsers.validation.displayNameMax')),
    roleType: z.enum([
      'admin',
      'support',
      'saler',
      'manager',
      'accountant',
      'affiliate',
    ]),
    sendInvite: z.boolean(),
  });
}

/**
 * Response envelope for POST /admin/users.
 * `invite_sent` reflects whether the email queue accepted the dispatch —
 * the success toast copy branches on this flag.
 */
export const CreateAdminUserResponseSchema = z.object({
  user: AdminUserRowSchema,
  invite_sent: z.boolean(),
});
export type CreateAdminUserResponse = z.infer<typeof CreateAdminUserResponseSchema>;

// ---------------------------------------------------------------------------
// Envelope unwrap (INTERFACE-STANDARDS message-priority)
// ---------------------------------------------------------------------------

export interface AdminUserApiError {
  ok: false;
  code: string;
  /** Human copy ready for toast — never AxiosError.message. */
  userMessage: string;
  fieldErrors?: Record<string, string[]>;
  requestId?: string;
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

const NETWORK_FALLBACK = 'Network did not respond — please try again.';

/**
 * Defensive envelope unwrap mirroring the finance.api.ts contract. Toast
 * consumers read `userMessage` directly per INTERFACE-STANDARDS — they
 * MUST NOT read `AxiosError.message` or `Response.statusText`.
 *
 * Message priority:
 *   1. error.user_message
 *   2. error.message
 *   3. message (legacy top-level)
 *   4. NETWORK_FALLBACK
 */
export function unwrapAdminUserEnvelope<TData>(payload: unknown):
  | { ok: true; data: TData; requestId?: string }
  | AdminUserApiError {
  if (payload == null || typeof payload !== 'object') {
    return { ok: false, code: 'NETWORK_ERROR', userMessage: NETWORK_FALLBACK };
  }
  const body = payload as Record<string, unknown>;

  // ---- success ----------------------------------------------------------
  if ((body.ok === true || body.success === true) && 'data' in body) {
    return {
      ok: true,
      data: body.data as TData,
      requestId:
        pickString(body.request_id) ?? pickString(body.requestId),
    };
  }

  // ---- error envelope ---------------------------------------------------
  const errRaw = body.error;
  if (
    (body.ok === false || body.success === false) &&
    errRaw &&
    typeof errRaw === 'object'
  ) {
    const err = errRaw as Record<string, unknown>;
    const out: AdminUserApiError = {
      ok: false,
      code: pickString(err.code) ?? 'UNKNOWN_ERROR',
      //  priority: user_message > message > top-level message
      userMessage:
        pickString(err.user_message) ??
        pickString(err.message) ??
        pickString(body.message) ??
        NETWORK_FALLBACK,
    };
    if (err.field_errors && typeof err.field_errors === 'object') {
      out.fieldErrors = err.field_errors as Record<string, string[]>;
    }
    const reqId = pickString(err.request_id) ?? pickString(body.request_id);
    if (reqId) out.requestId = reqId;
    return out;
  }

  return { ok: false, code: 'UNKNOWN_SHAPE', userMessage: NETWORK_FALLBACK };
}

// ---------------------------------------------------------------------------
// Local axios clone — keeps both `data` and `meta` accessible.
// ---------------------------------------------------------------------------

const rawAxios = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

// CSRF double-submit interceptor — the shared apiClient injects X-XSRF-TOKEN
// on its own instance, but this local clone bypasses that pipeline. Every
// mutating verb (POST/PUT/PATCH/DELETE) must echo the readable XSRF-TOKEN
// cookie back as the X-XSRF-TOKEN header or the BE csrf plugin rejects it
// with 403 AUTH_CSRF_MISSING (the create/patch/suspend/revoke calls all
// went out header-less before this — dbg-20260710 admin create-user 403).
rawAxios.interceptors.request.use((cfg) => {
  const method = (cfg.method ?? 'get').toUpperCase();
  if (
    method === 'POST' ||
    method === 'PUT' ||
    method === 'PATCH' ||
    method === 'DELETE'
  ) {
    const csrf = getCsrfToken();
    if (csrf) {
      cfg.headers = cfg.headers ?? {};
      // Both Axios v0.x (plain object) + v1.x (AxiosHeaders) shapes.
      const h = cfg.headers as {
        set?: (k: string, v: string) => void;
      } & Record<string, string>;
      if (typeof h.set === 'function') h.set('X-XSRF-TOKEN', csrf);
      else h['X-XSRF-TOKEN'] = csrf;
    }
  }
  return cfg;
});

function buildQs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.length === 0) continue;
    sp.set(k, String(v));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

/**
 * RFC 4122 v4 UUID — defensive fallback when `crypto.randomUUID` is
 * unavailable (older test envs). Used as the Idempotency-Key value on the
 * create + invite endpoints (invite resend MUST be idempotent
 * within the dedup window so a double-click doesn't dispatch two emails).
 */
export function newIdempotencyKey(): string {
  const c =
    typeof crypto !== 'undefined'
      ? (crypto as Crypto & { randomUUID?: () => string })
      : null;
  if (c?.randomUUID) return c.randomUUID();
  return (
    'idem_' + Math.random().toString(36).slice(2) + Date.now().toString(36)
  );
}

// ---------------------------------------------------------------------------
// LIST — GET /api/v1/admin/users
// ---------------------------------------------------------------------------

/**
 * Wire shape of the Phase 1 admin-users list handler. The BE returns
 * `{ users, pagination }` wrapped in the canonical `{success, data}`
 * envelope — we normalise to `{ rows, meta }` so consumers don't have to
 * know about the field rename.
 */
const ListAdminUsersHandlerShape = z.object({
  users: z.array(AdminUserRowSchema),
  pagination: AdminUserListMetaSchema,
});

export async function listAdminUsers(
  filters: AdminUserListFilters = {},
): Promise<AdminUserListPayload> {
  // Server-side query keys mirror the existing Phase 1 handler. The
  // handler accepts `roleType` (not `role`) per bug-15 fix on
  // 2026-05-04 — we translate at the boundary so callers stay on the
  // FE-natural `role` key.
  const params: Record<string, unknown> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? 20,
  };
  if (filters.search) params.search = filters.search;
  if (filters.role) params.roleType = filters.role;
  if (filters.status) params.status = filters.status;
  if (filters.sortBy) params.sortBy = filters.sortBy;
  if (filters.sortOrder) params.sortOrder = filters.sortOrder;

  const path = `/api/v1/admin/users${buildQs(params)}`;

  let body: unknown;
  try {
    const res = await rawAxios.get(path);
    body = res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies AdminUserApiError;
  }

  const unwrap = unwrapAdminUserEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;

  const parsed = ListAdminUsersHandlerShape.safeParse(unwrap.data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Response data did not match the expected format.',
    } satisfies AdminUserApiError;
  }
  return { rows: parsed.data.users, meta: parsed.data.pagination };
}

// ---------------------------------------------------------------------------
// CREATE — POST /api/v1/admin/users
// ---------------------------------------------------------------------------

/**
 * Issues POST /admin/users with a client-generated Idempotency-Key so an
 * accidental double-submit collapses to a single account creation +
 * single invite email. The dialog calls this from a TanStack
 * Query mutation; success invalidates the list cache.
 */
export async function createAdminUser(
  body: CreateAdminUserRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<CreateAdminUserResponse> {
  const parsed = CreateAdminUserRequestSchema.parse(body);

  // FE↔BE field-name mapping (dbg-20260710). The FE form/schema use the
  // FE-natural keys (displayName / roleType / sendInvite), but the BE zod
  // `PostAdminUsersRequest` expects primaryRole / firstName+lastName /
  // sendInviteEmail. Sending the FE shape raw dropped `primaryRole` (a
  // required field) → 400 VALIDATION_FAILED on every create. Translate at
  // the boundary; the BE recomposes a display name from first/last via its
  // `displayNameOf()` helper, so we split displayName on the last space.
  const trimmedName = parsed.displayName.trim();
  const lastSpace = trimmedName.lastIndexOf(' ');
  const firstName =
    lastSpace > 0 ? trimmedName.slice(0, lastSpace).trim() : trimmedName;
  const lastName = lastSpace > 0 ? trimmedName.slice(lastSpace + 1).trim() : undefined;
  const wireBody: Record<string, unknown> = {
    email: parsed.email,
    primaryRole: parsed.roleType,
    firstName,
    sendInviteEmail: parsed.sendInvite,
  };
  if (lastName) wireBody.lastName = lastName;

  let res: unknown;
  try {
    const r = await rawAxios.post('/api/v1/admin/users', wireBody, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies AdminUserApiError;
    }
  }

  const unwrap = unwrapAdminUserEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  // Response field-name mapping (dbg-20260710): the BE success envelope
  // returns `inviteEmailQueued` but the FE schema expects `invite_sent`.
  // Normalise before validating so a good 201 isn't rejected as
  // INVALID_PAYLOAD once the request-side 400 is fixed.
  const raw = unwrap.data;
  const normalised =
    raw && typeof raw === 'object' && !('invite_sent' in raw)
      ? {
          ...(raw as Record<string, unknown>),
          invite_sent: (raw as Record<string, unknown>).inviteEmailQueued ?? false,
        }
      : raw;

  const out = CreateAdminUserResponseSchema.safeParse(normalised);
  if (!out.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Account creation response had an invalid format.',
    } satisfies AdminUserApiError;
  }
  return out.data;
}

// ---------------------------------------------------------------------------
// INVITE — POST /api/v1/admin/users/:id/invite (resend / no-invite uplift)
// ---------------------------------------------------------------------------

export const InviteAdminUserResponseSchema = z.object({
  invite_sent: z.boolean(),
  /** Optional copy of the expiry deadline so the toast can show it. */
  expires_at: z.string().nullable().optional(),
});
export type InviteAdminUserResponse = z.infer<typeof InviteAdminUserResponseSchema>;

/**
 * POST /api/v1/admin/users/:id/invite — re-sends (or sends for the first
 * time) the invite email. Always carries an Idempotency-Key so a
 * double-click on the row action doesn't queue two emails for the same
 * user within the dedup window.
 */
export async function inviteAdminUser(
  userId: string,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<InviteAdminUserResponse> {
  let res: unknown;
  try {
    const r = await rawAxios.post(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/invite`,
      {},
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies AdminUserApiError;
    }
  }

  const unwrap = unwrapAdminUserEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  const out = InviteAdminUserResponseSchema.safeParse(unwrap.data);
  if (!out.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Invite email response had an invalid format.',
    } satisfies AdminUserApiError;
  }
  return out.data;
}

// ===========================================================================
// Phase 8.2 Wave 7 Task 50 — User Detail page endpoints.
// ===========================================================================
//
// vg-binding: PLAN:task-50
// vg-binding: P8.D-101  — Permissions resolved tree (effective_permissions[])
// vg-binding: P8.D-380  — Every mutation produces audit entry (visible in
//                          Activity tab on refresh)
// vg-binding: P8.D-475  — Self-action guard enforced at page layer; the
//                          API client is dumb transport.

export type AdminUserDetailStatus = 'active' | 'inactive' | 'locked';

/** Wire shape of GET /api/v1/admin/users/:id response data. */
export const AdminUserDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  primary_role: z.string(),
  additional_roles: z.array(z.string()).default([]),
  custom_permissions: z.array(z.string()).default([]),
  effective_permissions: z.array(z.string()).default([]),
  // Real accounts carry more statuses than the admin-created trio (e.g.
  // 'pending_survey', 'suspended', 'pending'). Accept any string; the detail
  // page already renders unknown statuses verbatim + falls back on the badge
  // map. Narrowing this here caused "invalid format" for tenant accounts.
  status: z.string(),
  mfa_enabled: z.boolean(),
  last_login_at: z.string().nullable(),
  last_login_ip: z.string().nullable(),
  last_active_page: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

/** Wire shape of GET /api/v1/admin/users/:id/activity response data row. */
export const AdminUserActivityEntrySchema = z.object({
  id: z.string(),
  // Derived FE category (login/role_change/security/page_visit). Kept as a
  // plain string — it is mapped from BE audit rows in getAdminUserActivity;
  // the Activity section renders unknown values verbatim + falls back on the
  // badge map.
  category: z.string(),
  /**
   * Raw audit action enum (`AUTH_LOGIN`, `billing.mode.switched`, …). Stays the
   * wire value — the Activity table translates it to a label via
   * `detail.activity.events.<sanitized>`. dbg-20260731-025626.
   */
  event_type: z.string(),
  ip: z.string().nullable(),
  user_agent: z.string().nullable(),
  device: z.string().nullable(),
  target_page: z.string().nullable(),
  role_diff: z
    .object({
      added: z.array(z.string()).default([]),
      removed: z.array(z.string()).default([]),
    })
    .nullable(),
  /**
   * What the event touched, derived from the audit row's `changes` blob. Was a
   * verbatim copy of `event_type`, so the Event and Details columns printed the
   * same raw enum twice. dbg-20260731-025626.
   */
  detail: z.string(),
  /** Audit target collection/model name, e.g. `user`, `TicketMessages`. */
  target_type: z.string().nullable(),
  /** Audit target document id. */
  target_id: z.string().nullable(),
  created_at: z.string(),
});
export type AdminUserActivityEntry = z.infer<
  typeof AdminUserActivityEntrySchema
>;

export const AdminUserActivityMetaSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
  total: z.number().int().min(0),
});
export type AdminUserActivityMeta = z.infer<typeof AdminUserActivityMetaSchema>;

export interface AdminUserActivityPage {
  rows: AdminUserActivityEntry[];
  meta: AdminUserActivityMeta;
}

export type AdminUserActivityCategory =
  | 'login'
  | 'role_change'
  | 'security'
  | 'page_visit'
  | 'all';

export interface AdminUserActivityQuery {
  page?: number;
  limit?: number;
  category?: AdminUserActivityCategory;
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/users/:id
// ---------------------------------------------------------------------------

export async function getAdminUserDetail(
  userId: string,
): Promise<AdminUserDetail> {
  let body: unknown;
  try {
    const r = await rawAxios.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}`,
    );
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies AdminUserApiError;
  }

  const unwrap = unwrapAdminUserEnvelope<unknown>(body);
  if (!unwrap.ok) throw unwrap;

  const parsed = AdminUserDetailSchema.safeParse(unwrap.data);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Account detail response had an invalid format.',
    } satisfies AdminUserApiError;
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/admin/users/:id — partial profile update.
// Accepts the BE-canonical snake_case keys (name/email/primary_role/status).
// ---------------------------------------------------------------------------

export interface UpdateAdminUserRequest {
  name?: string;
  email?: string;
  primary_role?: string;
  status?: AdminUserDetailStatus;
}

export async function updateAdminUser(
  userId: string,
  body: UpdateAdminUserRequest,
): Promise<void> {
  let res: unknown;
  try {
    const r = await rawAxios.patch(
      `/api/v1/admin/users/${encodeURIComponent(userId)}`,
      body,
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies AdminUserApiError;
    }
  }

  // BE PATCH returns { ok, user:{…}, permsVersionBumped } (not the full
  // AdminUserDetail contract). We only need success here — the caller refetches
  // the detail query to refresh the UI, so don't parse/return the body.
  const unwrap = unwrapAdminUserEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;
}

// ---------------------------------------------------------------------------
// GET /api/v1/admin/users/:id/activity?page=&limit=&category=
// ---------------------------------------------------------------------------

// The BE (Phase 8.2 getAdminUserActivityHandler) returns the STANDARD envelope
// `{ success, data: { items[], page, page_size, total, total_pages } }` with
// raw audit-log rows — NOT the `{ ok, data[], meta }` shape this file used to
// assume. Parse the real envelope + audit row, then map to the FE entry shape.
const BeAuditRowShape = z
  .object({
    id: z.string(),
    action: z.string().optional().default(''),
    category: z.string().nullable().optional().default(null),
    targetType: z.string().nullable().optional().default(null),
    targetId: z.string().nullable().optional().default(null),
    ip: z.string().nullable().optional().default(null),
    userAgent: z.string().nullable().optional().default(null),
    changes: z.unknown().optional(),
    createdAt: z.string(),
  })
  .passthrough();

const ActivityEnvelopeShape = z
  .object({
    success: z.literal(true),
    data: z
      .object({
        items: z.array(BeAuditRowShape).default([]),
        page: z.number().int().min(1).default(1),
        page_size: z.number().int().min(1).default(20),
        total: z.number().int().min(0).default(0),
      })
      .passthrough(),
  })
  .passthrough();

/** Map a BE audit action/category → the FE activity category enum. */
function deriveActivityCategory(action: string): AdminUserActivityCategory {
  const a = action.toUpperCase();
  if (a.includes('LOGIN') || a.includes('LOGOUT') || a.includes('OTP') || a.includes('2FA')) {
    return 'login';
  }
  if (a.includes('ROLE')) return 'role_change';
  if (a.includes('PAGE') || a.includes('VISIT')) return 'page_visit';
  return 'security';
}

/** Best-effort role-diff extraction from an audit `changes` blob. */
function extractRoleDiff(
  changes: unknown,
): { added: string[]; removed: string[] } | null {
  if (!changes || typeof changes !== 'object') return null;
  const after = (changes as { after?: unknown }).after;
  const diff =
    after && typeof after === 'object'
      ? (after as { diff?: unknown }).diff
      : undefined;
  if (diff && typeof diff === 'object') {
    const g = (diff as { granted?: unknown }).granted;
    const r = (diff as { revoked?: unknown }).revoked;
    const added = Array.isArray(g) ? g.map(String) : [];
    const removed = Array.isArray(r) ? r.map(String) : [];
    if (added.length || removed.length) return { added, removed };
  }
  return null;
}

/**
 * Compact, human-readable summary of an audit row's `changes.after` payload —
 * what the Details column should show.
 *
 * The old implementation set `detail = r.action`, i.e. the Event column's raw
 * enum repeated verbatim, so the row carried zero extra information and both
 * columns looked like keys. dbg-20260731-025626.
 *
 * Emits `key=value` pairs (scalars only, ids truncated) and skips the internal
 * `__meta` envelope the audit writer attaches.
 */
const DETAIL_SKIP_KEYS = new Set(['__meta']);
const DETAIL_MAX_PAIRS = 4;
const DETAIL_MAX_VALUE_LEN = 24;

function summarizeChanges(changes: unknown): string {
  if (!changes || typeof changes !== 'object') return '';
  const after = (changes as { after?: unknown }).after;
  const src =
    after && typeof after === 'object' ? (after as Record<string, unknown>) : null;
  if (!src) return '';

  const pairs: string[] = [];
  for (const [k, v] of Object.entries(src)) {
    if (pairs.length >= DETAIL_MAX_PAIRS) break;
    if (DETAIL_SKIP_KEYS.has(k)) continue;
    if (v === null || v === undefined) continue;
    if (typeof v === 'object') continue; // nested blobs stay out of a table cell
    let text = String(v);
    if (text.length > DETAIL_MAX_VALUE_LEN) {
      text = `${text.slice(0, DETAIL_MAX_VALUE_LEN)}…`;
    }
    pairs.push(`${k}=${text}`);
  }
  return pairs.join(' · ');
}

function mapBeRowToEntry(
  r: z.infer<typeof BeAuditRowShape>,
): AdminUserActivityEntry {
  return {
    id: r.id,
    category: deriveActivityCategory(r.action),
    event_type: r.action || 'unknown',
    ip: r.ip ?? null,
    user_agent: r.userAgent ?? null,
    // Device label comes from the UA string — the "IP / Device" column showed
    // only the IP because this was hardcoded null. dbg-20260731-025626.
    device: deriveDeviceLabel(r.userAgent ?? null),
    target_page: r.targetType === 'page' ? r.targetId ?? null : null,
    role_diff: extractRoleDiff(r.changes),
    detail: summarizeChanges(r.changes),
    target_type: r.targetType ?? null,
    target_id: r.targetId ?? null,
    created_at: r.createdAt,
  };
}

/**
 * Coarse browser/OS label from a User-Agent string. Deliberately crude — the
 * column only needs "which device was this", not full UA parsing, and pulling a
 * UA-parser dependency in for one table cell is not worth it.
 */
function deriveDeviceLabel(ua: string | null): string | null {
  if (!ua) return null;
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\/|Opera/.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) && /Version\//.test(ua) ? 'Safari'
    : /Firefox\//.test(ua) ? 'Firefox'
    : null;
  const os =
    /Windows/.test(ua) ? 'Windows'
    : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /Linux/.test(ua) ? 'Linux'
    : null;
  if (browser && os) return `${browser} · ${os}`;
  return browser ?? os ?? null;
}

export async function getAdminUserActivity(
  userId: string,
  query: AdminUserActivityQuery = {},
): Promise<AdminUserActivityPage> {
  // BE query contract: page + page_size (NOT `limit`), optional action_prefix.
  // Sending `limit`/`category` tripped the strict (additionalProperties:false)
  // query schema → HTTP 400 "must NOT have additional properties". Category is
  // derived per-row on the FE (BE audit rows have no such taxonomy), so it is
  // not forwarded as a server filter here.
  const params: Record<string, unknown> = {
    page: query.page ?? 1,
    page_size: query.limit ?? 20,
  };

  const path = `/api/v1/admin/users/${encodeURIComponent(userId)}/activity${buildQs(params)}`;

  let body: unknown;
  try {
    const r = await rawAxios.get(path);
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies AdminUserApiError;
  }

  const parsed = ActivityEnvelopeShape.safeParse(body);
  if (!parsed.success) {
    const unwrap = unwrapAdminUserEnvelope<unknown>(body);
    if (!unwrap.ok) throw unwrap;
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Activity response had an invalid format.',
    } satisfies AdminUserApiError;
  }
  const d = parsed.data.data;
  const rows = d.items.map(mapBeRowToEntry);
  return {
    rows,
    meta: { page: d.page, limit: d.page_size, total: d.total },
  };
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/users/:id/sessions/revoke
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// GET /api/v1/admin/users/:id/sessions — active sessions list (Sessions tab)
// ---------------------------------------------------------------------------

export const AdminUserSessionSchema = z.object({
  id: z.string(),
  ip: z.string().nullable(),
  user_agent: z.string().nullable(),
  created_at: z.string(),
  last_active_at: z.string(),
});
export type AdminUserSession = z.infer<typeof AdminUserSessionSchema>;

export async function getAdminUserSessions(
  userId: string,
): Promise<AdminUserSession[]> {
  let body: unknown;
  try {
    const r = await rawAxios.get(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions`,
    );
    body = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
    }
    throw {
      ok: false,
      code: 'NETWORK_ERROR',
      userMessage: NETWORK_FALLBACK,
    } satisfies AdminUserApiError;
  }
  const unwrap = unwrapAdminUserEnvelope<{ sessions?: unknown }>(body);
  if (!unwrap.ok) throw unwrap;
  const raw = (unwrap.data as { sessions?: unknown } | null)?.sessions ?? [];
  const parsed = z.array(AdminUserSessionSchema).safeParse(raw);
  if (!parsed.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Sessions response had an invalid format.',
    } satisfies AdminUserApiError;
  }
  return parsed.data;
}

export const RevokeAdminUserSessionsRequestSchema = z
  .object({
    scope: z.enum(['all', 'individual']),
    session_id: z.string().optional(),
  })
  .refine((v) => v.scope === 'all' || typeof v.session_id === 'string', {
    message: 'session_id is required when scope=individual',
    path: ['session_id'],
  });
export type RevokeAdminUserSessionsRequest = z.infer<
  typeof RevokeAdminUserSessionsRequestSchema
>;

export const RevokeAdminUserSessionsResponseSchema = z.object({
  user_id: z.string(),
  revoked_count: z.number().int().min(0),
  audit_log_id: z.string(),
});
export type RevokeAdminUserSessionsResponse = z.infer<
  typeof RevokeAdminUserSessionsResponseSchema
>;

export async function revokeAdminUserSessions(
  userId: string,
  body: RevokeAdminUserSessionsRequest,
  idempotencyKey: string = newIdempotencyKey(),
): Promise<RevokeAdminUserSessionsResponse> {
  const parsed = RevokeAdminUserSessionsRequestSchema.parse(body);

  let res: unknown;
  try {
    const r = await rawAxios.post(
      `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions/revoke`,
      parsed,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies AdminUserApiError;
    }
  }

  const unwrap = unwrapAdminUserEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;

  const out = RevokeAdminUserSessionsResponseSchema.safeParse(unwrap.data);
  if (!out.success) {
    throw {
      ok: false,
      code: 'INVALID_PAYLOAD',
      userMessage: 'Session revoke response had an invalid format.',
    } satisfies AdminUserApiError;
  }
  return out.data;
}

// ---------------------------------------------------------------------------
// Custom-role assignment (grant / revoke) — #1064/#1065 support
// ---------------------------------------------------------------------------

const RolesResponseSchema = z.object({ roles: z.array(z.string()).default([]) });

async function _rolesMutation(
  method: 'post' | 'delete',
  url: string,
  body?: unknown,
): Promise<string[]> {
  let res: unknown;
  try {
    const r =
      method === 'post'
        ? await rawAxios.post(url, body ?? {})
        : await rawAxios.delete(url);
    res = r.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      const unwrap = unwrapAdminUserEnvelope<unknown>(err.response.data);
      if (!unwrap.ok) throw unwrap;
      res = err.response.data;
    } else {
      throw {
        ok: false,
        code: 'NETWORK_ERROR',
        userMessage: NETWORK_FALLBACK,
      } satisfies AdminUserApiError;
    }
  }
  const unwrap = unwrapAdminUserEnvelope<unknown>(res);
  if (!unwrap.ok) throw unwrap;
  const out = RolesResponseSchema.safeParse(unwrap.data);
  return out.success ? out.data.roles : [];
}

/** Grant a custom admin role (by its `code` slug) to a user. */
export async function grantAdminUserRole(
  userId: string,
  code: string,
): Promise<string[]> {
  return _rolesMutation(
    'post',
    `/api/v1/admin/users/${encodeURIComponent(userId)}/roles`,
    { code },
  );
}

/** Revoke a custom admin role from a user. */
export async function revokeAdminUserRole(
  userId: string,
  code: string,
): Promise<string[]> {
  return _rolesMutation(
    'delete',
    `/api/v1/admin/users/${encodeURIComponent(userId)}/roles/${encodeURIComponent(code)}`,
  );
}

// ---------------------------------------------------------------------------
// Test seam
// ---------------------------------------------------------------------------

export const __test__ = {
  rawAxios,
  buildQs,
  newIdempotencyKey,
};
